/* To update the parameters, you must create a different version. It may break older queries otherwise */

import {
  newDateStringNoTimezone,
} from '@/components/mixins/MomentUtil'

const locationString = 'Location'
const mediaTypeString = 'MediaType'

const formats = {
  excel: 'Excel',
  csv: 'CSV',
}

export const missingTypes = {
  stations: 0,
  analytes: 1,
  guidelines: 2,
}

export const reportGroupings = {
  stationMedia: {
    name: 'Stations and Media Types',
    value: [locationString, mediaTypeString],
  },
  mediaTypes: {
    name: 'Media Types',
    value: [mediaTypeString],
  },
  locations: {
    name: 'Stations',
    value: [locationString],
  },
}

export const reportTypes = {
  standard: {
    value: 'standard',
    name: 'Standard',
    format: formats.excel,
    canGroupWorksheet: true,
    canIncludeStatSummary: false,
    reportGroupings: [
      reportGroupings.stationMedia,
      reportGroupings.mediaTypes,
    ],
  },
  excel: {
    value: 'excel',
    name: 'Wide',
    format: formats.excel,
    canGroupWorksheet: true,
    canIncludeStatSummary: true,
    reportGroupings: [
      reportGroupings.stationMedia,
      reportGroupings.locations,
      reportGroupings.mediaTypes,
    ],
  },
  csv: {
    value: 'csv',
    name: 'Long',
    format: formats.csv,
    canGroupWorksheet: false,
    canIncludeStatSummary: false,
    reportGroupings: [],
  },
}

export const defaultReportType = reportTypes.standard.value
export const defaultReportGrouping = reportTypes.standard.reportGroupings[0].value

export const reportTypesMapping = {
  [reportTypes.excel.value]: reportTypes.excel,
  [reportTypes.standard.value]: reportTypes.standard,
  [reportTypes.csv.value]: reportTypes.csv,
}

/**
 * The date range covered by the given analytes, as the first and last record of any of them.
 * Returns moments, so it can also feed the date range picker.
 */
export function analytesDateRange(analytes) {
  let start = null
  let end = null

  ;(analytes || []).forEach(analyte => {
    if (analyte.FirstSampleDate && (!start || analyte.FirstSampleDate.isBefore(start, 'days'))) {
      start = analyte.FirstSampleDate
    }
    if (analyte.LastSampleDate && (!end || analyte.LastSampleDate.isAfter(end, 'days'))) {
      end = analyte.LastSampleDate
    }
  })

  return {
    start,
    end,
  }
}

/**
 * An empty date range means "all available data", which the report generator cannot use.
 * Fall back to the range covered by the selected analytes.
 */
function makeEffectiveDateRange(selection) {
  let dateRange = selection.dateRange
  if (dateRange && dateRange.start && dateRange.end) return dateRange

  let available = analytesDateRange(selection.analytes)
  if (!available.start || !available.end) return dateRange

  return {
    start: newDateStringNoTimezone(available.start),
    end: newDateStringNoTimezone(available.end),
  }
}

export function makeRequestData(selection, reportOptions, serviceId) {
  let stations = []
  // forEach station
  selection.stations.forEach(station => {
    let mediaGuidelines = []
    // forEach mediType in that station
    if (!station.medias) return
    station.medias.forEach(mediaType => {
      // Make a mediaType 1 - many guidelines relationship
      let guidelineNames = selection.guidelines
        .filter(guideline => guideline.MediaTypes.has(mediaType))
        .map(guideline => guideline.GuidelineName)

      mediaGuidelines.push({
        mediaType,
        guidelineNames,
      })
    })

    stations.push({
      mediaGuidelines,
      stationName: station.name,
    })
  })

  // Happens if a station erroneously has no media types, then report generator fails
  if (stations.length == 0) {
    throw 'Cannot generate report. The selected stations have no data.'
  }
  var guidelineNames = selection.guidelines.map(x => x.GuidelineName)

  let requestData = {
    stations,
    serviceId,
    guidelines: guidelineNames,
    dateRange: makeEffectiveDateRange(selection),
    analytes: selection.analytes.map(analyte => analyte.AnalyteName),
    mediaTypes: selection.mediaTypes,
    includeQaQc: reportOptions.includeQaQc,
    reportFormat: reportOptions.reportFormat,
    includeEnvirodataAnalyteName: reportOptions.includeEnvirodataAnalyteName,
  }


  if ([reportTypes.excel.value, reportTypes.standard.value].includes(reportOptions.reportFormat)) {
    requestData = {
      ...requestData,
      organizeWorksheetBy: reportOptions.organizeWorksheetBy,
      includeSummaryTab: reportOptions.includeSummaryTab,
    }
  }

  return requestData
}

export function makeContinuousRequestData(selection, reportOptions, serviceId) {
  // Happens if a station erroneously has no media types, then report generator fails
  // if (selection.stations.length == 0) {
  //   throw 'Cannot generate report. The selected stations have no data.'
  // }


  let requestData = {
    continuousLocations: selection.stations.map(x => x.Identifier),
    serviceId,
    dateRange: selection.dateRange,
    timeSeries: selection.timeSeries.map(t => t.TimeSeriesIdentifer),
    reportFormat: reportOptions.reportFormat,
    includeGapMarkers: reportOptions.includeGaps,
  }


  if ([reportTypes.excel.value, reportTypes.standard.value].includes(reportOptions.reportFormat)) {
    requestData = {
      ...requestData,
      organizeWorksheetBy: reportOptions.organizeWorksheetBy,
      includeSummaryTab: reportOptions.includeSummaryTab,
    }
  }

  return requestData
}

export const changesNotSavedParam = {
  title: 'Changes not saved',
  text: 'Your current changes or selection will be lost. Are you sure?',
  confirmText: 'Discard',
  cancelText: 'Cancel',
  color: 'warning',
}

export function makeSelectionData(selection, missingParams) {
  let stationIds = [...new Set([...selection.stations.map(station => station.id), ...missingParams.stations])]
  let analyteNames = [...new Set([...selection.analytes.map(analyte => analyte.AnalyteName), ...missingParams.analytes])]
  let dateRange = selection.dateRange
  let guidelineIds = [...new Set([...selection.guidelines.map(guideline => guideline.Id), ...missingParams.guidelines])]

  return {
    stationIds,
    analyteNames,
    dateRange,
    guidelineIds,
    mediaTypes: selection.mediaTypes,
  }
}

export function makeQueryDetails(query) {
  if (!query) return {}
  const {
    // eslint-disable-next-line no-unused-vars
    queryParams,
    ...queryDetails
  } = query
  return queryDetails
}

export const emptyQuery = {
  queryParams: {
    selection: {
      stations: null,
      guidelines: null,
      analytes: null,
      dateRange: {
        start: null,
        end: null,
      },
    },
    reportOptions: null,
  },
}

export const saveStates = {
  createNew: {
    text: 'create new',
    color: 'success',
    icon: 'done',
    isUpdate: false,
  },
  overwriteExisting: {
    text: 'overwrite existing',
    color: 'warning',
    isUpdate: true,
    confirmParams: {
      title: 'Overwrite existing',
      text: 'This will overwrite an existing query not currently selected. Are you sure?',
      confirmText: 'Overwrite',
      cancelText: 'Cancel',
      color: 'warning',
    },
  },
  updateExisting: {
    text: 'update existing',
    color: 'success',
    isUpdate: true,
    confirmParams: {
      title: 'Update current query',
      text: 'This will update your current selected query. Are you sure?',
      confirmText: 'Update',
      cancelText: 'Cancel',
      color: 'success',
    },
  },
}


/**
 * Groupings [ { StationIds: [<int>], MediaTypes: [<string>] }]
 * A group of StationIds that have the same Media Types, since multiple stations can have the same media types.
 * This class helps index the data so you can fetch all media types of a station.
 */
export class StationMediaTypeGrouping {
  constructor(groupings) {
    if (!groupings) {
      this.mediaTypeGroups = []
      this.stationIndex = {}
      return
    }

    let mediaTypeGroups = []
    let stationIndex = {}

    let grouping
    for (let i = 0; i < groupings.length; i++) {
      grouping = groupings[i]
      mediaTypeGroups.push(grouping.MediaTypes)
      grouping.StationIds.forEach(stationId => stationIndex[stationId] = i)
    }

    this.mediaTypeGroups = mediaTypeGroups
    this.stationIndex = stationIndex
  }

  get(stationId) {
    let groupIndex = this.stationIndex[stationId]

    if (groupIndex == null) {
      return []
    }


    let mediaTypes = this.mediaTypeGroups[groupIndex]
    if (!mediaTypes) return []

    return mediaTypes
  }

  get allMediaTypes() {
    const all = new Set()
    this.mediaTypeGroups.forEach(group => {
      group.forEach(mt => all.add(mt))
    })
    return [...all]
  }
}